import { Modal, ModalWindow, UIHeading2 } from '@cian/ui-kit';
import { LoadableErrorBoundary } from '../../../../../packages/LoadableHelpers';
import { FILTER_NAME } from '../../constants';
import { AdvancedFiltersContentContainerLoadable } from '../AdvancedFiltersContentContainer';
import { AdvancedFiltersModalFooterContainer } from '../AdvancedFiltersModalFooterContainer';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { FC, useCallback } from 'react';
import { useAppDispatch, useJsonQueryBackup } from 'shared/hooks';
import { applyFilters } from 'shared/thunks/filters';
import { updateLastAppliedFilter } from 'shared/slices/searchMetaTooltip';
import { updateTags } from 'shared/thunks/tags';
import { trackAdvancedFiltersSearchClick, trackClearClick } from './tracking';
import { useSelector } from 'react-redux';
import { selectQuerystring } from 'shared/selectors/searchMeta';
import { setJsonQuery } from 'shared/slices/jsonQuery';
import { updateSearchMeta } from 'shared/thunks/searchMeta';
import { selectJsonQuery } from 'shared/selectors/jsonQuery';
import { getUncommonProperties } from 'shared/utils/jsonQuery';
import { updateLocalStorageFiltersWereApplied } from 'shared/utils/uxfbLocalStorage/uxfbLocalStorage';
import { resetTerms } from '../../../../../packages/JsonQuery/resetTerms';

interface IAdvancedFiltersModalContainerProps {
  isRedesignEnabled: boolean;
  onClose: VoidFunction;
}

export const AdvancedFiltersModalContainer: FC<IAdvancedFiltersModalContainerProps> = ({
  isRedesignEnabled,
  onClose,
}) => {
  const { logger } = useApplicationContext();

  const dispatch = useAppDispatch();
  const jsonQuery = useSelector(selectJsonQuery);
  const { resetJsonQuery } = useJsonQueryBackup();
  const queryString = useSelector(selectQuerystring);

  const handleClose = useCallback(() => {
    onClose();
    resetJsonQuery();
    dispatch(updateLastAppliedFilter(null));
  }, [dispatch, onClose, resetJsonQuery]);

  const handleFiltersApply = useCallback(() => {
    onClose();
    dispatch(updateTags());
    dispatch(applyFilters());
    trackAdvancedFiltersSearchClick(queryString);
    updateLocalStorageFiltersWereApplied();
  }, [dispatch, onClose, queryString, updateLocalStorageFiltersWereApplied]);

  const handleFiltersReset = useCallback(() => {
    const uncommonProperties = getUncommonProperties(jsonQuery) || [];
    const nextJsonQuery = resetTerms(jsonQuery)(uncommonProperties);

    dispatch(setJsonQuery(nextJsonQuery));
    dispatch(updateSearchMeta());
    trackClearClick();
  }, [dispatch, jsonQuery]);

  const handleRetry = () => AdvancedFiltersContentContainerLoadable.load();

  return isRedesignEnabled ? (
    <Modal
      size="L"
      open
      onOpenChange={handleClose}
      header={<UIHeading2>{FILTER_NAME}</UIHeading2>}
      footer={
        <AdvancedFiltersModalFooterContainer onFiltersReset={handleFiltersReset} onFiltersApply={handleFiltersApply} />
      }
    >
      <LoadableErrorBoundary logger={logger} onRetry={handleRetry}>
        <AdvancedFiltersContentContainerLoadable />
      </LoadableErrorBoundary>
    </Modal>
  ) : (
    <ModalWindow
      open
      onClose={handleClose}
      size="M"
      fixed
      width={952}
      header={<UIHeading2>{FILTER_NAME}</UIHeading2>}
      footer={
        <AdvancedFiltersModalFooterContainer onFiltersReset={handleFiltersReset} onFiltersApply={handleFiltersApply} />
      }
    >
      <LoadableErrorBoundary logger={logger} onRetry={handleRetry}>
        <AdvancedFiltersContentContainerLoadable />
      </LoadableErrorBoundary>
    </ModalWindow>
  );
};
